/**
 * The colour vocabulary shared by Typography and Icon, lifted here by ENG-4835
 * to break a cycle rather than add one: Icon imported `TextColorType` from
 * Typography, and Typography now needs `RampColorToken`, which lived in Icon.
 *
 * Consumers reach these through the `@repo/ui` barrel, where all 40 existing
 * call sites already import them from — so nothing downstream changes.
 */

/**
 * A raw ramp token, for steps the semantic names cannot reach — icon artwork and
 * PDF pills are often tinted with an exact value that has no semantic name.
 * Prefer a semantic name when one fits.
 *
 * Spelled out per family rather than `${family}-${shade}` because the ramps are
 * not uniform: blue skips 700/800 but has 900, orange has a 450, red a 150.
 * Keep in sync with `../styles/tokens/color.css`.
 */
export type RampColorToken =
  | `${'warm-grey' | 'cold-grey'}-${100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900}`
  | `green-${100 | 200 | 300 | 400 | 500}`
  | `purple-${100 | 200 | 300 | 400 | 500}`
  | `orange-${100 | 200 | 300 | 400 | 450 | 500}`
  | `red-${100 | 150 | 200 | 300 | 400 | 500}`
  | `blue-${100 | 200 | 300 | 400 | 500 | 600 | 900}`
  | `yellow-${100 | 200 | 300 | 400 | 500 | 900}`
  | 'white'
  | 'chart-red';

export enum TextColor {
  GREEN = 'green',
  DARKGREEN = 'darkGreen',
  PURPLE = 'purple',
  DARKPURPLE = 'darkPurple',
  BLUE = 'blue',
  DARKBLUE = 'darkBlue',
  DARK = 'dark',
  LIGHT_GREY = 'lightGrey',
  LIGHTER_GREY = 'lighterGrey',
  RED = 'red',
  DARKRED = 'darkRed',
  ORANGE = 'orange',
  DARKORANGE = 'darkOrange',
  GREY = 'grey',
  WHITE = 'white',
}

export type TextColorType =
  | 'green'
  | 'darkGreen'
  | 'purple'
  | 'darkPurple'
  | 'blue'
  | 'darkBlue'
  | 'dark'
  | 'lightGrey'
  | 'lighterGrey'
  | 'red'
  | 'darkRed'
  | 'orange'
  | 'darkOrange'
  | 'grey'
  | 'white';

/**
 * Semantic colour -> ramp token. `Typography.module.css` holds the canonical
 * mapping as CSS classes; this mirrors it for components that cannot use them
 * (Icon tints via a custom property on a wrapper). Keep the two in sync.
 */
export const SEMANTIC_COLOR_TOKEN: Record<TextColorType, RampColorToken> = {
  green: 'green-300',
  darkGreen: 'green-400',
  red: 'red-300',
  darkRed: 'red-400',
  purple: 'purple-300',
  darkPurple: 'purple-400',
  blue: 'blue-300',
  darkBlue: 'blue-500',
  orange: 'orange-300',
  darkOrange: 'orange-400',
  dark: 'cold-grey-800',
  grey: 'warm-grey-700',
  lightGrey: 'warm-grey-500',
  lighterGrey: 'warm-grey-100',
  white: 'white',
};

/** True for the semantic vocabulary, false for a raw ramp token. */
export const isSemanticColor = (
  color: TextColorType | RampColorToken,
): color is TextColorType => color in SEMANTIC_COLOR_TOKEN;

/**
 * The ramp token name (no `var()`) for either kind of input. Prefer the CSS
 * classes where a component can use them, so callers can still out-specify.
 */
export const resolveColorToken = (
  color: TextColorType | RampColorToken,
): RampColorToken =>
  isSemanticColor(color) ? SEMANTIC_COLOR_TOKEN[color] : color;
